import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { CurrencyProvider } from "@/lib/currency";
import { CompanyProvider } from "@/lib/company";
import { FavoritesProvider } from "@/lib/favorites";
import { LanguageProvider } from "@/lib/i18n";
import Layout from "@/components/Layout";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RegisterChoice from "@/pages/RegisterChoice";
import RegisterBuyer from "@/pages/RegisterBuyer";
import RegisterSeller from "@/pages/RegisterSeller";
import ForgotPassword from "@/pages/ForgotPassword";
import Listing from "@/pages/Listing";
import ItemDetail from "@/pages/ItemDetail";
import Cart from "@/pages/Cart";
import { PaymentSuccess, PaymentCancel, PawaPayReturn, FlutterwaveReturn } from "@/pages/Payment";
import BankTransferInstructions from "@/pages/BankTransfer";
import LegalNotice from "@/pages/LegalNotice";
import CGV from "@/pages/CGV";
import Invoice from "@/pages/Invoice";
import SellerDashboard from "@/pages/SellerDashboard";
import SellerVerification from "@/pages/SellerVerification";
import SellerSubscription from "@/pages/SellerSubscription";
import ShopDetail from "@/pages/ShopDetail";
import PersonalShopper from "@/pages/PersonalShopper";
import AdminPersonalShopper from "@/pages/AdminPersonalShopper";
import AdminOrderAvailability from "@/pages/AdminOrderAvailability";
import PSQuoteDetail from "@/pages/PSQuoteDetail";
import Messages from "@/pages/Messages";
import Orders from "@/pages/Orders";
import Categories from "@/pages/Categories";
import Search from "@/pages/Search";
import Profile from "@/pages/Profile";
import Favorites from "@/pages/Favorites";
import Notifications from "@/pages/Notifications";
import Directory from "@/pages/Directory";
import ChatWidget from "@/components/ChatWidget";
import WhatsAppButton from "@/components/WhatsAppButton";

// Admin surface is bundled separately: buyers/visitors/sellers never download it.
// The route only pays for the chunk when an admin actually navigates to /admin/*.
const AdminDashboard        = lazy(() => import("@/pages/AdminDashboard"));
const AdminCustomers        = lazy(() => import("@/pages/AdminCustomers"));
const AdminCampaigns        = lazy(() => import("@/pages/AdminCampaigns"));

// Minimal fallback while an admin lazy-chunk is being fetched — no external
// dependency, matches the app's off-white background.
const AdminFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center bg-[#F5F5F0]" data-testid="admin-lazy-fallback">
    <div className="text-[#4B5563] text-sm animate-pulse">Chargement…</div>
  </div>
);

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
          <CompanyProvider>
          <CurrencyProvider>
            <CartProvider>
            <FavoritesProvider>
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/connexion" element={<Login />} />
                <Route path="/inscription" element={<RegisterChoice />} />
                <Route path="/inscription/acheteur" element={<RegisterBuyer />} />
                <Route path="/inscription/vendeur" element={<RegisterSeller />} />
                <Route path="/inscription/legacy" element={<Register />} />
                <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
                <Route path="/produits" element={<Listing kind="product" />} />
                <Route path="/services" element={<Listing kind="service" />} />
                <Route path="/produit/:id" element={<ItemDetail kind="product" />} />
                <Route path="/service/:id" element={<ItemDetail kind="service" />} />
                <Route path="/panier" element={<Cart />} />
                <Route path="/paiement/succes" element={<PaymentSuccess />} />
                <Route path="/paiement/annule" element={<PaymentCancel />} />
                <Route path="/paiement/pawapay-retour" element={<PawaPayReturn />} />
                <Route path="/paiement/flutterwave-retour" element={<FlutterwaveReturn />} />
                <Route path="/virement/:id" element={<BankTransferInstructions />} />
                <Route path="/mentions-legales" element={<LegalNotice />} />
                <Route path="/cgv" element={<CGV />} />
                <Route path="/facture/:id" element={<Invoice />} />
                <Route path="/vendeur" element={<SellerDashboard />} />
                <Route path="/vendeur/verification" element={<SellerVerification />} />
                <Route path="/vendeur/abonnement" element={<SellerSubscription />} />
                <Route path="/admin" element={<Suspense fallback={<AdminFallback />}><AdminDashboard /></Suspense>} />
                <Route path="/admin/personal-shopper" element={<AdminPersonalShopper />} />
                <Route path="/admin/disponibilite" element={<AdminOrderAvailability />} />
                <Route path="/admin/clients" element={<Suspense fallback={<AdminFallback />}><AdminCustomers /></Suspense>} />
                <Route path="/admin/campagnes" element={<Suspense fallback={<AdminFallback />}><AdminCampaigns /></Suspense>} />
                <Route path="/messagerie" element={<Messages />} />
                <Route path="/commandes" element={<Orders />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/recherche" element={<Search />} />
                <Route path="/profil" element={<Profile />} />
                <Route path="/favoris" element={<Favorites />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/boutiques" element={<Directory />} />
                <Route path="/boutique/:sid" element={<ShopDetail />} />
                <Route path="/personal-shopper" element={<PersonalShopper />} />
                <Route path="/personal-shopper/devis/:qid" element={<PSQuoteDetail />} />
              </Routes>
            </Layout>
            <ChatWidget />
            <WhatsAppButton />
            <CookieBanner />
            </FavoritesProvider>
            <Toaster position="bottom-right" richColors />
          </CartProvider>
          </CurrencyProvider>
          </CompanyProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
